/**
 * Monat je Apartment als eigene Ansicht des Grundkalender-Reiters: Wald-Kopf mit Apartment- und
 * Monatswechsel, darunter Raster und Seiten-Editor. Ein Wechsel mit Eingaben im Editor fragt
 * vorher nach; ohne Eingaben bleibt die Auswahl über den Monatswechsel stehen.
 */
import { SurfaceLoadError, SurfaceLoading, SurfaceStaleHint } from '@/shared/components';
import { problemText, useApiProblem } from '@/shared/api';
import { TransmissionChip } from './AvailabilityHeader';
import { useAutomationLayer } from './automation/AutomationLayerContext';
import { ModeChip } from './automation/AutomationMarks';
import { AutomationProblemHint } from './automation/AutomationProblemHint';
import { DiscardChangesDialog } from './calendar/DiscardChangesDialog';
import { MonthHeader } from './month/MonthHeader';
import { MonthMobileExtras } from './month/MonthMobileExtras';
import { MonthView } from './month/MonthView';
import { monthOf, shiftMonth } from './month/monthModel';
import { useAvailabilityMonth, type MonthHandover } from './useAvailabilityMonth';
import type { useAvailabilityPage } from './useAvailabilityPage';

export type { MonthHandover };

interface Props {
  page: ReturnType<typeof useAvailabilityPage>;
  handover: MonthHandover | null;
}

export function AvailabilityMonth({ page, handover }: Props) {
  const { calendar } = page;
  const month = useAvailabilityMonth(page, handover);
  const { apartmentId, change } = month;
  const problem = useApiProblem(calendar.error);
  const mode = useAutomationLayer()?.mode(apartmentId) ?? null;
  const retry = () => void calendar.refetch();
  return (
    <div className="min-w-0">
      <MonthHeader
        month={page.month}
        apartments={month.options}
        apartmentId={apartmentId}
        chips={
          <span className="flex flex-wrap gap-1.5">
            {mode && <ModeChip mode={mode} prefix="Preisautomatik: " />}
            {month.summary && <TransmissionChip summary={month.summary} />}
          </span>
        }
        onApartment={(id) => change({ apartment: id, day: undefined })}
        onMonth={(delta) => change({ month: shiftMonth(page.month, delta), day: undefined })}
        onToday={() => change({ month: monthOf(page.today), day: undefined })}
        onTab={(tab) =>
          change({
            tab: tab === 'channels' ? tab : undefined,
            apartment: undefined,
            month: undefined,
            day: undefined,
          })
        }
      />
      <div className="relative z-10 -mt-[74px] flex flex-col gap-3.5 md:-mt-[84px] md:px-5">
        {calendar.data && calendar.error && (
          <SurfaceStaleHint onRetry={retry} retrying={calendar.isFetching} />
        )}
        {!calendar.data ? (
          <section className="bg-card border-hairline rounded-tile shadow-float overflow-hidden border">
            {calendar.error ? (
              <SurfaceLoadError
                message={problemText(problem, 'Der Monat konnte nicht geladen werden.')}
                onRetry={retry}
                retrying={calendar.isFetching}
              />
            ) : (
              <SurfaceLoading label="Monat wird geladen" />
            )}
          </section>
        ) : (
          <MonthView
            key={`${apartmentId}-${month.resetToken}`}
            propertyGroupId={page.group!.id}
            data={calendar.data}
            apartmentId={apartmentId}
            canEdit={page.canEdit}
            loading={calendar.isPlaceholderData}
            initialRanges={month.initialRanges}
            onHandoverApplied={month.markHandoverUsed}
            onChanged={page.invalidate}
            onDirtyChange={month.editor.setDirty}
            notice={<AutomationProblemHint />}
            onManageRules={() =>
              change({ tab: 'channels', apartment: undefined, month: undefined, day: undefined })
            }
            mobileExtras={
              <MonthMobileExtras
                data={calendar.data}
                apartmentId={apartmentId}
                canFullSync={page.canFull}
                onChanged={page.invalidate}
              />
            }
          />
        )}
      </div>
      <DiscardChangesDialog {...month.editor.dialog} />
    </div>
  );
}
